import { jsxs, jsx } from 'react/jsx-runtime';
import { useOrganizationList } from '@clerk/clerk-react';
import { Building2, ChevronRight } from 'lucide-react';
import { useRef, useEffect } from 'react';
import { Skeleton } from '../ui/skeleton.js';

/**
 * Pick which organization to work in.
 *
 * Replaces Clerk's `OrganizationSwitcher` dropdown on the landing page. A
 * dropdown is a fine *switcher* once you're inside, but as the front door
 * it hides the decision behind a click and gives no sense of what you have
 * access to. This lists the organizations as rows you choose from.
 *
 * This is convenience, not a security boundary. Which organization is
 * active decides what the UI *asks* for; what a caller may actually read
 * or change is enforced server-side per request. A picker must never be
 * the thing keeping two customers' data apart.
 */
function OrganizationChooser({ onSelected, autoSelectSingle = true, className = '', }) {
    const { isLoaded, userMemberships, setActive } = useOrganizationList({
        userMemberships: { infinite: true },
    });
    const memberships = userMemberships?.data ?? [];
    // Guards against the auto-select effect firing twice while `setActive`
    // is still in flight, which would race two session updates.
    const autoSelectedRef = useRef(false);
    useEffect(() => {
        if (!isLoaded || !autoSelectSingle || autoSelectedRef.current)
            return;
        if (memberships.length !== 1 || !setActive)
            return;
        autoSelectedRef.current = true;
        void setActive({ organization: memberships[0].organization.id }).then(() => onSelected?.());
    }, [isLoaded, autoSelectSingle, memberships, setActive, onSelected]);
    if (!isLoaded) {
        return (jsxs("div", { className: `space-y-2 ${className}`, children: [jsx(Skeleton, { className: "h-16 w-full" }), jsx(Skeleton, { className: "h-16 w-full" })] }));
    }
    if (memberships.length === 0)
        return null;
    // Mid-flight for a single-org user: the effect above is already entering
    // it, so showing a one-row list would flash a choice that isn't one.
    if (autoSelectSingle && memberships.length === 1) {
        return (jsx("div", { className: `space-y-2 ${className}`, children: jsx(Skeleton, { className: "h-16 w-full" }) }));
    }
    return (jsx("ul", { className: `space-y-2 ${className}`, children: memberships.map((membership) => {
            const organization = membership.organization;
            return (jsx("li", { children: jsxs("button", { type: "button", onClick: () => {
                        if (!setActive)
                            return;
                        void setActive({ organization: organization.id }).then(() => onSelected?.());
                    }, className: "flex w-full items-center gap-3 rounded-lg border bg-card p-4 text-left transition-colors hover:bg-muted/50", children: [organization.imageUrl ? (jsx("img", { src: organization.imageUrl, alt: "", className: "h-10 w-10 shrink-0 rounded-md object-cover" })) : (jsx("span", { className: "flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10", children: jsx(Building2, { className: "h-5 w-5 text-primary" }) })), jsxs("span", { className: "min-w-0 flex-1", children: [jsx("span", { className: "block truncate font-medium", children: organization.name }), jsx("span", { className: "block truncate text-xs text-muted-foreground", children: formatRole(membership.role) })] }), jsx(ChevronRight, { className: "h-4 w-4 shrink-0 text-muted-foreground" })] }) }, organization.id));
        }) }));
}
/** `org:hunts_admin` → `Hunts admin`. */
function formatRole(role) {
    const bare = role.replace(/^org:/, '').replace(/_/g, ' ');
    return bare.charAt(0).toUpperCase() + bare.slice(1);
}

export { OrganizationChooser };
//# sourceMappingURL=OrganizationChooser.js.map
